import { ProductProviderSettings, ProductTemplateProviderType, TradeType, UserRole } from '@shared/models';

export type ProductProviderFileKey =
  | 'heidenreichFileName'
  | 'heidenreichDiscountFileName'
  | 'ahlsellFileName'
  | 'ahlsellDiscountFileName'
  | 'berggardAmundsenFileName'
  | 'berggardAmundsenDiscountFileName'
  | 'logiqOrganizationNumber'
  | 'solarFileName'
  | 'solarDiscountFileName';

export interface ProductProviderFileField {
  key: ProductProviderFileKey;
  label: string;
  hint: string;
  placeholder?: string;
}

export interface ProductProviderConfiguration {
  type: 'nobbFilters' | 'files';
  icon: string;
  title: string;
  description: string;
  hint?: string;
  // all file fields are required for the synchronization
  fileFields?: ProductProviderFileField[];
}

export interface ProductProviderConfigurationModalData {
  configuration: ProductProviderConfiguration;
  settings: ProductProviderSettings;
}

export interface ProductProviderIntegration {
  provider: ProductTemplateProviderType;
  name: string;
  description: string;
  logo?: string;
  website?: { url: string; label: string };
  countryCode: string;
  country: string;
  dataType: string;
  tradeTypes: TradeType[];
  enabledKey: keyof ProductProviderSettings;
  syncingKey: keyof ProductProviderSettings;
  configuration?: ProductProviderConfiguration;
  // global catalogues are shared by all companies and synchronized from admin integrations management
  globalSync?: boolean;
  syncRoles: UserRole[];
}

// provider names are brand names and are not translated
export const PRODUCT_TEMPLATE_PROVIDER_NAMES: Record<ProductTemplateProviderType, string> = {
  [ProductTemplateProviderType.None]: 'None',
  [ProductTemplateProviderType.Nobb]: 'NOBB',
  [ProductTemplateProviderType.Grunndata]: 'Grunndata',
  [ProductTemplateProviderType.Heidenreich]: 'Heidenreich',
  [ProductTemplateProviderType.Ahlsell]: 'Ahlsell',
  [ProductTemplateProviderType.BerggardAmundsen]: 'Berggård Amundsen',
  [ProductTemplateProviderType.Logiq]: 'Logiq',
  [ProductTemplateProviderType.Solar]: 'Solar',
};

// All providers currently deliver Norwegian data (NOK prices)
export const PRODUCT_PROVIDER_INTEGRATIONS: ProductProviderIntegration[] = [
  {
    provider: ProductTemplateProviderType.Nobb,
    name: 'masterData.productProviderSettings.nobb',
    description: 'masterData.productProviderSettings.nobbDescription',
    logo: 'assets/images/integrations/nobb.svg',
    website: { url: 'https://nobb.no', label: 'nobb.no' },
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.productCatalogue',
    tradeTypes: [TradeType.Building, TradeType.Plumbing],
    enabledKey: 'nobbEnabled',
    syncingKey: 'isNobbSyncing',
    globalSync: true,
    configuration: {
      type: 'nobbFilters',
      icon: 'pi pi-filter',
      title: 'masterData.productProviderSettings.nobbConfigurationTitle',
      description: 'masterData.productProviderSettings.nobbConfigurationDescription',
      hint: 'masterData.productProviderSettings.nobbConfigurationHint',
    },
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
  {
    provider: ProductTemplateProviderType.Grunndata,
    name: 'masterData.productProviderSettings.grunndata',
    description: 'masterData.productProviderSettings.grunndataDescription',
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.unionPrices',
    tradeTypes: [TradeType.Plumbing],
    enabledKey: 'grunndataEnabled',
    syncingKey: 'isGrunndataSyncing',
    globalSync: true,
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
  {
    provider: ProductTemplateProviderType.Heidenreich,
    name: 'masterData.productProviderSettings.heidenreich',
    description: 'masterData.productProviderSettings.heidenreichDescription',
    logo: 'assets/images/integrations/heidenreich.svg',
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.priceList',
    tradeTypes: [TradeType.Plumbing],
    enabledKey: 'heidenreichEnabled',
    syncingKey: 'isHeidenreichSyncing',
    configuration: {
      type: 'files',
      icon: 'pi pi-file',
      title: 'masterData.productProviderSettings.heidenreichConfigurationTitle',
      description: 'masterData.productProviderSettings.heidenreichConfigurationDescription',
      fileFields: [
        {
          key: 'heidenreichFileName',
          label: 'masterData.productProviderSettings.heidenreichFileName',
          hint: 'masterData.productProviderSettings.heidenreichFileNameHint',
        },
        {
          key: 'heidenreichDiscountFileName',
          label: 'masterData.productProviderSettings.heidenreichDiscountFileName',
          hint: 'masterData.productProviderSettings.heidenreichDiscountFileNameHint',
        },
      ],
    },
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
  {
    provider: ProductTemplateProviderType.Ahlsell,
    name: 'masterData.productProviderSettings.ahlsell',
    description: 'masterData.productProviderSettings.ahlsellDescription',
    logo: 'assets/images/integrations/ahlsell.svg',
    website: { url: 'https://www.ahlsell.no', label: 'ahlsell.no' },
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.priceList',
    tradeTypes: [TradeType.Plumbing, TradeType.Electricity, TradeType.Ventilation],
    enabledKey: 'ahlsellEnabled',
    syncingKey: 'isAhlsellSyncing',
    configuration: {
      type: 'files',
      icon: 'pi pi-file',
      title: 'masterData.productProviderSettings.ahlsellConfigurationTitle',
      description: 'masterData.productProviderSettings.ahlsellConfigurationDescription',
      fileFields: [
        {
          key: 'ahlsellFileName',
          label: 'masterData.productProviderSettings.ahlsellFileName',
          hint: 'masterData.productProviderSettings.ahlsellFileNameHint',
          placeholder: 'Pris_VVS_EFO40.txt',
        },
        {
          key: 'ahlsellDiscountFileName',
          label: 'masterData.productProviderSettings.ahlsellDiscountFileName',
          hint: 'masterData.productProviderSettings.ahlsellDiscountFileNameHint',
        },
      ],
    },
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
  {
    provider: ProductTemplateProviderType.BerggardAmundsen,
    name: 'masterData.productProviderSettings.berggardAmundsen',
    description: 'masterData.productProviderSettings.berggardAmundsenDescription',
    website: { url: 'https://www.berggardamundsen.no', label: 'berggardamundsen.no' },
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.priceList',
    tradeTypes: [TradeType.Electricity],
    enabledKey: 'berggardAmundsenEnabled',
    syncingKey: 'isBerggardAmundsenSyncing',
    configuration: {
      type: 'files',
      icon: 'pi pi-file',
      title: 'masterData.productProviderSettings.berggardAmundsenConfigurationTitle',
      description: 'masterData.productProviderSettings.berggardAmundsenConfigurationDescription',
      fileFields: [
        {
          key: 'berggardAmundsenFileName',
          label: 'masterData.productProviderSettings.berggardAmundsenFileName',
          hint: 'masterData.productProviderSettings.berggardAmundsenFileNameHint',
          placeholder: 'N4VARKPL.TXT',
        },
        {
          key: 'berggardAmundsenDiscountFileName',
          label: 'masterData.productProviderSettings.berggardAmundsenDiscountFileName',
          hint: 'masterData.productProviderSettings.berggardAmundsenDiscountFileNameHint',
          placeholder: 'Rabatt_N4_239931.rab',
        },
      ],
    },
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
  {
    provider: ProductTemplateProviderType.Logiq,
    name: 'masterData.productProviderSettings.logiq',
    description: 'masterData.productProviderSettings.logiqDescription',
    website: { url: 'https://www.logiqconnect.com', label: 'logiqconnect.com' },
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.priceList',
    tradeTypes: [TradeType.Plumbing],
    enabledKey: 'logiqEnabled',
    syncingKey: 'isLogiqSyncing',
    configuration: {
      type: 'files',
      icon: 'pi pi-building',
      title: 'masterData.productProviderSettings.logiqConfigurationTitle',
      description: 'masterData.productProviderSettings.logiqConfigurationDescription',
      fileFields: [
        {
          key: 'logiqOrganizationNumber',
          label: 'masterData.productProviderSettings.logiqOrganizationNumber',
          hint: 'masterData.productProviderSettings.logiqOrganizationNumberHint',
          placeholder: '884260922',
        },
      ],
    },
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
  {
    provider: ProductTemplateProviderType.Solar,
    name: 'masterData.productProviderSettings.solar',
    description: 'masterData.productProviderSettings.solarDescription',
    website: { url: 'https://www.solar.no', label: 'solar.no' },
    countryCode: 'no',
    country: 'masterData.productProviderSettings.norway',
    dataType: 'masterData.productProviderSettings.priceList',
    tradeTypes: [TradeType.Electricity],
    enabledKey: 'solarEnabled',
    syncingKey: 'isSolarSyncing',
    configuration: {
      type: 'files',
      icon: 'pi pi-file',
      title: 'masterData.productProviderSettings.solarConfigurationTitle',
      description: 'masterData.productProviderSettings.solarConfigurationDescription',
      fileFields: [
        {
          key: 'solarFileName',
          label: 'masterData.productProviderSettings.solarFileName',
          hint: 'masterData.productProviderSettings.solarFileNameHint',
          placeholder: 'V4varefil.zip',
        },
        {
          key: 'solarDiscountFileName',
          label: 'masterData.productProviderSettings.solarDiscountFileName',
          hint: 'masterData.productProviderSettings.solarDiscountFileNameHint',
          placeholder: 'R4rabatt.txt',
        },
      ],
    },
    syncRoles: [UserRole.Admin, UserRole.Ceo],
  },
];
